import { UseFormReturn } from 'react-hook-form'
import { z } from 'zod'

import { NativeAssetInfo } from '@/config/chain/types'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { AssetInputSchema, normalizeDialogFormValues } from '@/features/dialogs/common/logic/form'
import { FormFieldsForDialog } from '@/features/dialogs/common/types'
import { AaveData } from '@/features/sparklend/common/data/market-info/aave-data-layer/query'
import { MarketInfo } from '@/features/sparklend/common/data/market-info/marketInfo'
import { updatePositionSummary } from '@/features/sparklend/common/data/market-info/updatePositionSummary'
import { MarketWalletInfo } from '@/features/sparklend/common/data/useMarketWalletInfo'
import { getWithdrawMaxValue } from '@/features/sparklend/common/utils/action-max-value-getters/getWithdrawMaxValue'
import {
  validateWithdraw,
  withdrawalValidationIssueToMessage,
} from '@/features/sparklend/common/validation/validateWithdraw'

export interface GetWithdrawDialogFormValidatorOptions {
  marketInfo: MarketInfo
  aaveData: AaveData
  nativeAssetInfo: NativeAssetInfo
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getWithdrawDialogFormValidator({
  marketInfo,
  aaveData,
  nativeAssetInfo,
}: GetWithdrawDialogFormValidatorOptions) {
  return AssetInputSchema.superRefine((field, ctx) => {
    const formWithdrawAsset = normalizeDialogFormValues(field, marketInfo)
    const reserve = marketInfo.findOneReserveBySymbol(formWithdrawAsset.token.symbol)
    const deposited = marketInfo.findOnePositionBySymbol(formWithdrawAsset.token.symbol).collateralBalance
    const hasZeroLtvCollateral = marketInfo.reserves.some((r) => r.usageAsCollateralEnabledOnUser && r.maxLtv.isZero())

    const updatedUserSummary = updatePositionSummary({
      withdrawals: [formWithdrawAsset],
      marketInfo,
      aaveData,
      nativeAssetInfo,
    })

    const validationIssue = validateWithdraw({
      value: formWithdrawAsset.value,
      asset: {
        status: reserve.status,
        unborrowedLiquidity: reserve.unborrowedLiquidity,
        maxLtv: reserve.maxLtv,
        eModeCategory: reserve.eModeCategory,
      },
      user: {
        deposited,
        liquidationThreshold: updatedUserSummary.currentLiquidationThreshold,
        ltvAfterWithdrawal: updatedUserSummary.loanToValue,
        eModeState: marketInfo.userConfiguration.eModeState,
        hasZeroLtvCollateral,
        isBorrowingAny: updatedUserSummary.totalBorrowsUSD.gt(0),
      },
    })
    if (validationIssue) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: withdrawalValidationIssueToMessage[validationIssue],
        path: ['value'],
      })
    }
  })
}

export function getFormFieldsForWithdrawDialog(
  form: UseFormReturn<AssetInputSchema>,
  marketInfo: MarketInfo,
  walletInfo: MarketWalletInfo,
): FormFieldsForDialog {
  // eslint-disable-next-line func-style
  const changeAsset = (newSymbol: TokenSymbol): void => {
    form.setValue('symbol', newSymbol)
    form.setValue('value', '')
    form.setValue('isMaxSelected', false)
    form.clearErrors()
  }

  const { symbol, value } = form.getValues()
  const position = marketInfo.findOnePositionBySymbol(symbol)

  const maxWithdrawValue = getWithdrawMaxValue({
    user: {
      deposited: position.collateralBalance,
      healthFactor: marketInfo.userPositionSummary.healthFactor,
      totalBorrowsUSD: marketInfo.userPositionSummary.totalBorrowsUSD,
      eModeState: marketInfo.userConfiguration.eModeState,
    },
    asset: {
      status: position.reserve.status,
      liquidationThreshold: position.reserve.liquidationThreshold,
      unborrowedLiquidity: position.reserve.unborrowedLiquidity,
      unitPriceUsd: position.reserve.token.unitPriceUsd,
      decimals: position.reserve.token.decimals,
      usageAsCollateralEnabledOnUser: position.reserve.usageAsCollateralEnabledOnUser,
      eModeCategory: position.reserve.eModeCategory,
    },
  })

  const token = marketInfo.findOneBySymbol(symbol)
  const balance = walletInfo.findWalletBalanceForSymbol(symbol)

  return {
    selectedAsset: token,
    selectedAssetBalance: balance,
    value,
    maxSelectedFieldName: 'isMaxSelected',
    changeAsset,
    maxValue: maxWithdrawValue,
  }
}
